import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiPatch } from "@/lib/apiClient";
import { withKbMembership } from "@/lib/knowledgeBaseRegistry";
import {
  articlePath,
  knowledgeStatusLabel,
  libraryPath,
  OTHER_LIBRARY_ID,
  type KnowledgeArticle,
} from "@/lib/knowledgeModel";
import { agentsHref } from "@/lib/atwAgentsHost";
import {
  KnowledgeChrome,
  KnowledgeError,
  KnowledgeNeedsBase,
  knowledgePrimaryLinkClass,
  knowledgeSecondaryLinkClass,
  knowledgeTableWrapClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useKnowledgeArticles } from "@/pages/knowledge/useKnowledgeArticles";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";
import { useKnowledgeRouteBase } from "@/pages/knowledge/useKnowledgeRouteBase";

export function BookPage() {
  const { bookKey: rawKey = "" } = useParams();
  const bookKey = decodeURIComponent(rawKey);
  const { books: customBooks } = useCustomBooks();
  const { activeId, hasBases } = useKnowledgeBases();
  const baseId = useKnowledgeRouteBase() || activeId;
  const custom = customBooks.find((book) => book.collectionKey === bookKey);
  const { articles, setArticles, loading, error, reload } = useKnowledgeArticles();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { inBook, available } = useMemo(() => {
    const inside: KnowledgeArticle[] = [];
    const outside: KnowledgeArticle[] = [];
    for (const article of articles) {
      if (article.collectionKeys.includes(bookKey)) inside.push(article);
      else outside.push(article);
    }
    return { inBook: inside, available: outside };
  }, [articles, bookKey]);

  async function setMembership(article: KnowledgeArticle, include: boolean) {
    setBusyId(article.id);
    setActionError(null);
    const nextKeys = include
      ? withKbMembership([...article.collectionKeys, bookKey], activeId)
      : withKbMembership(
          article.collectionKeys.filter((key) => key !== bookKey),
          activeId,
        );
    const res = await apiPatch<KnowledgeArticle>(
      `/api/knowledge/articles/${encodeURIComponent(article.id)}`,
      { collectionKeys: nextKeys },
    );
    setBusyId(null);
    if (!res.ok || !res.data?.id) {
      setActionError(res.error || "Could not update this book.");
      return;
    }
    setArticles((prev) =>
      prev.map((row) => (row.id === res.data?.id ? { ...row, ...res.data } : row)),
    );
  }

  const libraryId = OTHER_LIBRARY_ID;
  const title = custom?.title || bookKey;
  const bookDescription =
    custom?.description?.trim() ||
    "Add or remove this collection key on existing articles. The article body stays in the knowledge base.";

  if (!hasBases) {
    return (
      <KnowledgeChrome
        title={title}
        description="Books live inside a knowledge base. Create a knowledge base first."
        badge="Same articles"
        showBaseSwitcher={false}
      >
        <KnowledgeNeedsBase />
      </KnowledgeChrome>
    );
  }

  return (
    <KnowledgeChrome
      title={title}
      description={bookDescription}
      badge={bookKey}
      actions={
        <>
          <Link to={agentsHref("/knowledge/books")} className={knowledgeSecondaryLinkClass}>
            All books
          </Link>
          <Link to={agentsHref(libraryPath(baseId, libraryId))} className={knowledgeSecondaryLinkClass}>
            Library
          </Link>
          <Link to={agentsHref("/knowledge/articles")} className={knowledgePrimaryLinkClass}>
            Articles
          </Link>
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {actionError ? <KnowledgeError message={actionError} /> : null}
      <MembershipTable
        heading={`In ${title}`}
        empty={
          articles.length === 0 ? "No articles yet. Create one first." : "No articles in this book yet."
        }
        rows={inBook}
        loading={loading}
        busyId={busyId}
        actionLabel="Remove"
        onAction={(article) => void setMembership(article, false)}
        baseId={baseId}
      />
      <MembershipTable
        heading="Add from the knowledge base"
        empty={
          articles.length === 0
            ? "No articles yet. Create one first."
            : "Every article is already in this book."
        }
        rows={available}
        loading={loading}
        busyId={busyId}
        actionLabel="Add"
        onAction={(article) => void setMembership(article, true)}
        baseId={baseId}
      />
      <button
        type="button"
        onClick={() => void reload()}
        className="text-sm text-indigo-300 hover:underline"
      >
        Refresh
      </button>
    </KnowledgeChrome>
  );
}

function MembershipTable({
  heading,
  empty,
  rows,
  loading,
  busyId,
  actionLabel,
  onAction,
  baseId,
}: {
  heading: string;
  empty: string;
  rows: KnowledgeArticle[];
  loading: boolean;
  busyId: string | null;
  actionLabel: string;
  onAction: (article: KnowledgeArticle) => void;
  baseId: string;
}) {
  return (
    <div className={knowledgeTableWrapClass}>
      <h2 className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900 dark:border-white/10 dark:text-white">
        {heading}
      </h2>
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-zinc-900/50 dark:text-zinc-500">
          <tr>
            <th className="px-4 py-3 font-semibold">Title</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 text-right font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={3}
                className="px-4 py-8 text-center text-slate-500 dark:text-zinc-500"
              >
                Loading articles…
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={3}
                className="px-4 py-8 text-center text-slate-500 dark:text-zinc-500"
              >
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((article) => (
              <tr
                key={article.id}
                className="border-b border-slate-100 dark:border-white/5"
              >
                <td className="px-4 py-3 font-medium">
                  <Link
                    to={agentsHref(articlePath(baseId, article.id))}
                    className="text-indigo-600 hover:underline dark:text-indigo-300"
                  >
                    {article.title || "Untitled"}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-700 dark:text-zinc-300">
                  {knowledgeStatusLabel(article.status)}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    disabled={busyId === article.id}
                    onClick={() => onAction(article)}
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 dark:border-white/20 dark:text-zinc-100 dark:hover:bg-white/5 disabled:opacity-50"
                  >
                    {busyId === article.id ? "Saving…" : actionLabel}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
